import React, { useState } from 'react';
import { announcementContent } from '../../data/announcementContent';
import HeaderNewClear from '../partials/HeaderNewClear.jsx';
import FooterNew from '../partials/FooterNew.jsx';
import ctaBg from '../../styles/img/announcement/cta-bg.png';

// ASTRNT 2.0 Design System Tokens
const NAVY        = '#18244E';
const NAVY_50     = '#F0F6FE';
const NAVY_200    = '#C1DBFC';
const RED         = '#C04051';
const RED_HOVER   = '#872A3C';
const WHITE       = '#FFFFFF';
const BODY_BG     = '#F8F9FE';
const NEUTRAL_200 = '#E6E6E6';
const NEUTRAL_500 = '#767676';
const NEUTRAL_900 = '#1A1A1A';

const FEATURE_ICON_BG = ['#fef3c7', '#fce7f3', '#fef9c3', '#ffedd5'];

// ─── Pill badge (red) ─────────────────────────────────────────────────────────
const PillBadge = ({ children }) => (
  <div style={{
    display: 'inline-block',
    backgroundColor: RED,
    color: WHITE,
    padding: '4px 14px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: 'Inter, sans-serif',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: '16px',
  }}>
    {children}
  </div>
);

// ─── FAQ accordion item ───────────────────────────────────────────────────────
const FaqItem = ({ question, answer, isLast }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: isLast ? 'none' : `1px solid ${NEUTRAL_200}`, padding: '20px 0' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          width: '100%', background: 'none', border: 'none',
          cursor: 'pointer', padding: 0, textAlign: 'left', gap: '16px',
        }}
      >
        <span style={{
          fontSize: '16px', fontWeight: '600',
          color: NEUTRAL_900, fontFamily: 'Inter, sans-serif', lineHeight: '1.5',
        }}>
          {question}
        </span>
        <span style={{
          flexShrink: 0, width: '28px', height: '28px', borderRadius: '50%',
          backgroundColor: open ? NAVY : NAVY_50,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color 0.15s ease',
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>
            <path d="M2 5L7 10L12 5"
              stroke={open ? WHITE : NAVY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      {open && (
        <p style={{
          marginTop: '12px', marginBottom: 0,
          fontSize: '14px', color: NEUTRAL_500,
          lineHeight: '1.7', fontFamily: 'Inter, sans-serif',
        }}>
          {answer}
        </p>
      )}
    </div>
  );
};

// ─── FAQ section group ────────────────────────────────────────────────────────
const FaqSection = ({ sectionTitle, faqs }) => (
  <div style={{ marginBottom: '28px' }}>
    <div style={{
      display: 'inline-flex', alignItems: 'center',
      backgroundColor: NAVY_50, color: NAVY,
      padding: '3px 10px', borderRadius: '4px',
      fontSize: '12px', fontWeight: '600',
      fontFamily: 'Inter, sans-serif',
      letterSpacing: '0.05em', marginBottom: '12px',
      textTransform: 'uppercase',
    }}>
      {sectionTitle}
    </div>
    <div style={{
      backgroundColor: WHITE, border: `1px solid ${NEUTRAL_200}`,
      borderRadius: '8px', padding: '0 24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {faqs.map((faq, idx) => (
        <FaqItem key={idx} question={faq.question} answer={faq.answer} isLast={idx === faqs.length - 1} />
      ))}
    </div>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const AnnouncementFaq = () => {
  const { hero, features, faqSections, cta } = announcementContent;

  const inner = (maxW = '1120px') => ({
    maxWidth: maxW,
    margin: '0 auto',
    padding: '0 40px',
  });

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', color: NEUTRAL_900 }}>
      <HeaderNewClear isOnStatusWork="is-on" />

      {/* Spacer for fixed header (~120px) */}
      <div className="pad-top" />

      {/* ── Hero ── */}
      <div style={{
        position: 'relative',
        backgroundColor: NAVY,
        color: WHITE,
        padding: '120px 40px 100px',
        textAlign: 'center',
        backgroundImage: `url(${hero.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay',
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(24,36,78,0.85)', zIndex: 1,
        }} />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{
            display: 'inline-block',
            backgroundColor: 'rgba(193,219,252,0.12)',
            border: '1px solid rgba(193,219,252,0.3)',
            color: NAVY_200,
            padding: '4px 14px', borderRadius: '9999px',
            fontSize: '12px', fontWeight: '600',
            letterSpacing: '0.06em', textTransform: 'uppercase',
            marginBottom: '28px',
          }}>
            Platform update
          </div>

          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: '600',
            marginBottom: '20px', lineHeight: '1.1',
            letterSpacing: '-0.025em', color: WHITE,
            maxWidth: '560px', margin: '0 auto 20px',
          }}>
            {hero.title}
          </h1>

          <p style={{
            fontSize: '16px', lineHeight: '1.7',
            color: 'rgba(255,255,255,0.68)',
            maxWidth: '480px', margin: '0 auto 40px',
          }}>
            {hero.subtitle}
          </p>

          <div style={{
            display: 'flex', justifyContent: 'center',
            gap: '12px', flexWrap: 'wrap',
          }}>
            {['Intelligent Onboarding', 'A Unified Talent Hub', 'ASTRNT Recommends', 'Frictionless Hiring'].map((tag) => (
              <div key={tag} style={{
                padding: '6px 14px',
                borderRadius: '9999px',
                border: '1px solid rgba(193,219,252,0.25)',
                backgroundColor: 'rgba(193,219,252,0.08)',
                color: 'rgba(255,255,255,0.75)',
                fontSize: '13px',
                fontWeight: '500',
                fontFamily: 'Inter, sans-serif',
              }}>
                {tag}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── What's new ── */}
      <div style={{ backgroundColor: BODY_BG, padding: '80px 0', width: '100%', boxSizing: 'border-box' }}>
        <div style={inner('1120px')}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <PillBadge>What's new</PillBadge>
            <h2 style={{
              fontSize: '30px', fontWeight: '600',
              color: NEUTRAL_900, letterSpacing: '-0.01em', marginBottom: '12px',
            }}>
              Built for how you actually work
            </h2>
            <p style={{ color: NEUTRAL_500, fontSize: '16px', lineHeight: '1.6' }}>
              Every change is grounded in real user feedback and designed to reduce friction across your workflow.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '20px',
          }}>
            {features.map((feature, idx) => (
              <div key={idx} style={{
                backgroundColor: WHITE,
                borderRadius: '16px',
                border: '1px solid #e5e7eb',
                padding: '32px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              }}>
                <div style={{
                  width: '56px', height: '56px',
                  backgroundColor: FEATURE_ICON_BG[idx],
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '24px',
                }}>
                  <img src={feature.icon} alt="" width="28" height="28" />
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                  {feature.title}
                </h3>
                <p style={{ fontSize: '13.6px', color: '#4b5563', lineHeight: '26px', margin: 0 }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={{ backgroundColor: WHITE, padding: '80px 0', width: '100%', boxSizing: 'border-box' }}>
        <div style={inner('680px')}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <PillBadge>FAQ</PillBadge>
            <h2 style={{
              fontSize: '30px', fontWeight: '600',
              color: NEUTRAL_900, letterSpacing: '-0.01em', marginBottom: '12px',
            }}>
              Frequently asked questions
            </h2>
            <p style={{ color: NEUTRAL_500, fontSize: '16px', lineHeight: '1.6' }}>
              Everything you need to know about what's changing and what's not.
            </p>
          </div>

          {faqSections.map((section, idx) => (
            <FaqSection key={idx} sectionTitle={section.sectionTitle} faqs={section.faqs} />
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <div style={{
        backgroundImage: `url(${ctaBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '80px 0',
        width: '100%',
        boxSizing: 'border-box',
        textAlign: 'center',
        color: WHITE,
      }}>
        <div style={inner('560px')}>
          <h2 style={{
            fontSize: '30px', fontWeight: '600',
            marginBottom: '12px', letterSpacing: '-0.01em',
          }}>
            Ready to experience it yourself?
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.72)', fontSize: '16px',
            marginBottom: '32px', lineHeight: '1.6',
          }}>
            Log in to the new dashboard and see the difference firsthand.
          </p>
          <a
            href={cta.link}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '55px',
              padding: '0 24px',
              backgroundColor: RED,
              color: WHITE,
              fontSize: '16px',
              fontWeight: '500',
              fontFamily: 'Inter, sans-serif',
              borderRadius: '6px',
              textDecoration: 'none',
              transition: 'background-color 0.15s ease-in-out',
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = RED_HOVER; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = RED; }}
          >
            {cta.text}
          </a>
        </div>
      </div>

      <FooterNew />
    </div>
  );
};

export default AnnouncementFaq;
